<script lang="ts">
	import Button from "../Button.svelte";
	import LabelledDate from "../Form/LabelledDate.svelte";
	import cake from "../../../../core/assets/icons/cake.svg?raw";
	import LabelledInput from "../Form/LabelledInput.svelte";
	import GdprCheckbox from "../Form/GdprCheckbox.svelte";
	import Checkbox from "../Form/Checkbox.svelte";
	import Form from "../Form/Form.svelte";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import type { WeddingFormInput } from "../../../../core/schema/WeddingForm.js";
	import { getClients } from "../../appsync/getClients.js";
	import sendWeddingForm from "../../appsync/mutations/sendWeddingForm.graphql";
	import Modal from "../Modal.svelte";
	import Smartform from "../Smartform.svelte";
	import { WeddingFormStatus } from "../../../../core/schema/WeddingFormStatus.js";

	let asCompany = false;

	const globalSettings = getGlobalSettings();
	const { TENANT_ID } = globalSettings;

	const send = createPromiseStore(async (input: WeddingFormInput) => {
		const { graphQL } = await getClients(globalSettings);
		await graphQL.mutate<{ input: WeddingFormInput }>({
			mutation: sendWeddingForm,
			variables: { input },
		});
	});

	let showSuccessModal = false;
	async function submit(event: SubmitEvent): Promise<void> {
		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);

		const input: WeddingFormInput = {
			fullName: String(formData.get("fullName")),
			email: String(formData.get("email")),
			phoneNumber: String(formData.get("phoneNumber")),
			weddingLocation: String(formData.get("weddingLocation")),
			weddingDate: String(formData.get("weddingDate")),
			cuttingTime: String(formData.get("cuttingTime")),
			estimatedGuests: Number(formData.get("estimatedGuests")),
			cakeDeliveredByMyself: Boolean(formData.get("cakeDeliveredByMyself")),
			billingAddress: {
				street: String(formData.get("street")),
				streetNumber: String(formData.get("streetNumber")),
				city: String(formData.get("city")),
				zip: String(formData.get("zip")),
			},
			...(asCompany && {
				company: {
					name: String(formData.get("cmpName")),
					cin: String(formData.get("cmpCin")),
					vat: String(formData.get("cmpVat")),
				},
			}),
			fillingTasteTest: Boolean(formData.get("fillingTasteTest")),
			note: String(formData.get("note")) || undefined,
			tenantId: TENANT_ID,
			status: WeddingFormStatus.Undecided,
			gdprApprovedAt: String(formData.get("gdprApprovedAt")),
		};
		await send.call(input);
		form.reset();
		showSuccessModal = true;
	}

	// eslint-disable-next-line no-warning-comments
	// FIXME remove this after upgrade to Svelte 5.
	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<Modal show={showSuccessModal}>
	<p class="h2" slot="title">Děkujeme za poptávku</p>
	<div class="flex flex-col items-center gap-2">
		<p>Vaše poptávka byla úspěšně odeslána. Brzy se vám ozveme.</p>

		<Button
			on:click={() => {
				showSuccessModal = false;
			}}
		>
			Rozumím
		</Button>
	</div>
</Modal>

<Smartform>
	<Form on:submit={submit} class="flex flex-col items-center gap-8">
		<div class="w-full">
			<p>Osobní údaje</p>
			<div class="mt-4 grid w-full items-end gap-x-6 gap-y-3 lg:grid-flow-col lg:grid-rows-3">
				<LabelledInput id="fullName" required autocomplete="name">Jméno a příjmení</LabelledInput>
				<LabelledInput id="email" required type="email" autocomplete="email">E-mail</LabelledInput>
				<LabelledInput
					id="phoneNumber"
					required
					type="tel"
					autocomplete="tel"
					inputProps={{ pattern: "[+]?[\\d ]+", title: "Telefonní číslo" }}
				>
					Telefon
				</LabelledInput>
				<LabelledInput id="weddingLocation" required>Místo svatby</LabelledInput>
				<div class="grid gap-3 sm:grid-cols-2">
					<LabelledDate
						class="[&>input]:font-semibold"
						id="weddingDate"
						required
						clearable
						min={new Date()}
						withoutDayOfTheWeek
					>
						Datum svatby
					</LabelledDate>
					<LabelledInput id="cuttingTime" type="time" required>Čas krájení</LabelledInput>
				</div>
				<div class="grid gap-3 sm:grid-cols-2">
					<LabelledInput id="estimatedGuests" type="number" required inputProps={{ min: 1 }}>
						Přibližný počet hostů
					</LabelledInput>
					<Checkbox
						id="cakeDeliveredByMyself"
						class="relative !mt-0 h-[3.5rem] items-center rounded-[.25rem] border-[0.0625rem] bg-white px-3 text-base text-black lg:px-6"
						labelProps={{
							class:
								"absolute inset-0 w-full h-full cursor-pointer flex items-center pl-10 lg:pl-14 peer-checked:font-semibold",
						}}
					>
						<span class="!mt-[.15rem]">Dort si odvezu sám</span>
					</Checkbox>
				</div>
			</div>
		</div>

		<div class="mt-2 w-full">
			<div class="grid w-full items-end gap-x-6 gap-y-3 lg:grid-flow-col lg:grid-rows-3">
				<p class="mb-2">Fakturační údaje</p>

				<div class="row-span-2 grid gap-3 sm:row-span-1 sm:grid-cols-[1fr,7.5rem]">
					<LabelledInput
						id="street"
						required
						autocomplete="billing address-line1"
						inputProps={{ class: "smartform-company-address-street" }}
					>
						Ulice
					</LabelledInput>
					<LabelledInput
						id="streetNumber"
						required
						autocomplete="billing address-line2"
						inputProps={{ class: "smartform-company-address-number" }}
					>
						čp.
					</LabelledInput>
				</div>
				<div class="row-span-2 grid gap-3 sm:row-span-1 sm:grid-cols-[1fr,7.5rem]">
					<LabelledInput
						id="city"
						required
						autocomplete="billing address-level2"
						inputProps={{ class: "smartform-company-address-city" }}
					>
						Město
					</LabelledInput>
					<LabelledInput
						id="zip"
						required
						minlength={3}
						autocomplete="billing postal-code"
						inputProps={{ class: "smartform-company-address-zip" }}
					>
						PSČ
					</LabelledInput>
				</div>

				<Checkbox class="mb-2" id="asCompany" bind:checked={asCompany}>Fakturuji na firmu</Checkbox>
				<div
					class="contents {asCompany
						? ''
						: '[&>*]:pointer-events-none [&>*]:absolute [&>*]:opacity-0 [&>*]:lg:relative'}"
				>
					<LabelledInput id="cmpName" required disabled={!asCompany} inputProps={{ class: "smartform-company-name" }}>
						Název firmy
					</LabelledInput>
					<div class="row-span-2 grid gap-3 sm:row-span-1 sm:grid-cols-2">
						<LabelledInput
							id="cmpCin"
							required
							disabled={!asCompany}
							inputProps={{
								pattern: "\\d+",
								class: "smartform-company-registration-number",
							}}
						>
							IČO
						</LabelledInput>
						<LabelledInput id="cmpVat" disabled={!asCompany} inputProps={{ class: "smartform-company-vat-number" }}>
							DIČ
						</LabelledInput>
					</div>
				</div>
			</div>
		</div>

		<div class="relative mt-4 w-full">
			<div class="grid w-full items-end gap-x-6 lg:grid-flow-col lg:grid-cols-2">
				<Checkbox
					id="fillingTasteTest"
					class="relative !mt-0 h-[3.5rem] items-center rounded-[.25rem] border-[0.0625rem] bg-white px-3 text-base text-black lg:px-6"
					labelProps={{
						class:
							"absolute inset-0 w-full h-full cursor-pointer flex items-center pl-10 lg:pl-14 peer-checked:font-semibold",
					}}
				>
					<span class="!mt-[.15rem]">Mám zájem o ochutnávku náplní</span>
				</Checkbox>
			</div>
		</div>

		<div class="relative w-full">
			<textarea
				id="note"
				name="note"
				class="input-with-label peer h-[15.5625rem] w-full rounded-[.25rem] border border-gray-200 px-3 !pb-4 !pt-[2.25rem] text-base focus:font-semibold focus:outline-none lg:px-6"
				placeholder=" "
			></textarea>
			<label class="label textarea-label" for="note">Poznámka k objednávce</label>
			<p class="text-2xs mt-3">*Povinné údaje</p>
		</div>

		<GdprCheckbox />

		<Button
			class="sm:!text-md mx-auto !rounded-full !px-8 !py-2 sm:!px-12 sm:!py-4 lg:mx-0 xl:!px-28 xl:!py-[1.125rem] xl:!text-[1.375rem]"
			type="submit"
			icon={cake}
			iconClass="h-6"
			disabled={$send.status === "loading"}
		>
			{#if $send.status === "loading"}
				Odesílám…
			{:else}
				Odeslat formulář
			{/if}
		</Button>
	</Form>
</Smartform>
